import { parseISO, format, endOfMonth, startOfMonth, addDays } from 'date-fns';

import { isValidStay } from './stay';
import { isValidDates } from './valid-dates';
import { formatDate } from '@spa/shared/utils/date';
import { formatMoney } from '@spa/shared/utils/money';
import { validAvailabilityRules } from './availability-rules';
import { CONTRACT_TYPE } from '@spa/shared/constants/contractType';

const CONTRACT_CONDITIONS = {
  [CONTRACT_TYPE.MONTHLY]: {
    moveInDays: Array.from({ length: 12 }).map((_, index) => 20 + index),
    moveOutDays: Array.from({ length: 10 }).map((_, index) => 1 + index),
    suggestStart: start => addDays(endOfMonth(start), 1),
    suggestEnd: end => addDays(startOfMonth(end), -1),
    messages: {
      options: { rent: 1 },
      start: 'If you select <span>{{moveIn}}</span> as your move-in, you’ll save a month of rent ({{price}}).',
      end: 'If you select <span>{{moveOut}}</span> as your move-out, you’ll save a month of rent ({{price}}).',
      both:
        'If you select <span>{{moveIn}}</span> as your move-in and <span>{{moveOut}}</span> as your move-out, you’ll save two months of rent ({{price}}).'
    }
  },
  [CONTRACT_TYPE.FORTNIGHT]: {
    moveInDays: [
      ...Array.from({ length: 5 }).map((_, index) => 10 + index),
      ...Array.from({ length: 6 }).map((_, index) => 25 + index)
    ],
    moveOutDays: [
      ...Array.from({ length: 5 }).map((_, index) => 1 + index),
      ...Array.from({ length: 5 }).map((_, index) => 16 + index)
    ],
    suggestStart: start =>
      parseInt(format(start, 'dd'), 10) > 15 ? addDays(endOfMonth(start), 1) : addDays(startOfMonth(start), 15),
    suggestEnd: end =>
      parseInt(format(end, 'dd'), 10) > 15 ? addDays(startOfMonth(end), 14) : addDays(startOfMonth(end), -1),
    messages: {
      options: { rent: 0.5 },
      start: 'If you select <span>{{moveIn}}</span> as your move-in, you’ll save half a month of rent ({{price}}).',
      end: 'If you select <span>{{moveOut}}</span> as your move-out, you’ll save half a month of rent ({{price}}).',
      both:
        'If you select <span>{{moveIn}}</span> as your move-in and <span>{{moveOut}}</span> as your move-out, you’ll save one month of rent ({{price}}).'
    }
  }
};

const DATE_FORMAT = 'MMMM dd yyyy';

const getPricing = (ratio, language, rentMoveIn, rentMoveOut) => {
  const currency = rentMoveIn?.currency_code ?? rentMoveOut?.currency_code;
  if (rentMoveIn && rentMoveOut) {
    return formatMoney((rentMoveIn.amount + rentMoveOut.amount) * ratio, currency, language);
  }

  return formatMoney((rentMoveIn?.amount ?? rentMoveOut?.amount) * ratio, currency, language);
};

export const getMessage = (moveIn, moveOut, rents, { start, end, both, options: { rent } }, language) => {
  const rentMoveIn = moveIn && rents[parseInt(format(moveIn, 'MM'), 10) - 1];
  const rentMoveOut = moveOut && rents[parseInt(format(moveOut, 'MM'), 10) - 1];

  if (moveIn && moveOut) {
    const price = getPricing(rent, language, rentMoveIn, rentMoveOut);

    return {
      message: both,
      extra: {
        moveIn: formatDate(moveIn, language, DATE_FORMAT),
        moveOut: formatDate(moveOut, language, DATE_FORMAT),
        price
      }
    };
  }

  const price = getPricing(rent, language, rentMoveIn, rentMoveOut);

  return moveIn
    ? { message: start, extra: { moveIn: formatDate(moveIn, language, DATE_FORMAT), price } }
    : { message: end, extra: { moveOut: formatDate(moveOut, language, DATE_FORMAT), price } };
};

export const isValidSuggestion = (moveIn, moveOut, availabilityRules, availableFrom, minimumStay, maximumStay, t) => {
  const dates = isValidDates(moveIn, moveOut);
  const stay = isValidStay(moveIn, moveOut, minimumStay, maximumStay, t);
  const rules = validAvailabilityRules(t, 'en', availabilityRules, availableFrom, moveIn, moveOut);

  return dates.isValid && stay.isValid && rules.isValid;
};

export const contract = (
  start = null,
  end = null,
  t,
  language,
  rents,
  contractType,
  availabilityRules = [],
  availableFrom,
  minimumStay,
  maximumStay
) => {
  if (!start || !end || ![CONTRACT_TYPE.MONTHLY, CONTRACT_TYPE.FORTNIGHT].includes(contractType)) {
    return {};
  }

  const dateStart = parseISO(start);
  const dateEnd = parseISO(end);
  const { moveInDays, moveOutDays, suggestStart, suggestEnd, messages } = CONTRACT_CONDITIONS[contractType];

  const moveIn = moveInDays.includes(parseInt(format(dateStart, 'dd'), 10)) ? suggestStart(dateStart) : null;
  const moveOut = moveOutDays.includes(parseInt(format(dateEnd, 'dd'), 10)) ? suggestEnd(dateEnd) : null;

  if (
    (!moveIn && !moveOut) ||
    !isValidSuggestion(
      moveIn ?? dateStart,
      moveOut ?? dateEnd,
      availabilityRules,
      availableFrom,
      minimumStay,
      maximumStay,
      t
    )
  ) {
    return {};
  }

  const { message, extra } = getMessage(moveIn, moveOut, rents, messages, language);

  return {
    dates: { moveIn: moveIn ? formatDate(moveIn) : start, moveOut: moveOut ? formatDate(moveOut) : end },
    contractType,
    message: t(message, extra)
  };
};

export const getSuggestions = (...props) => ({ ...contract(...props) });
